import { DSPAuthError, dspAuthService } from './DSPAuthService';

const EXPIRY_SKEW_MS = 60 * 1000;

const AUTH_EXPIRED_CODES = new Set(['session_expired', 'token_refresh_failed']);

class ClientTokenCache {
  constructor() {
    this.clientToken = null;
    this.expiresAt = 0;
    this.inFlightPromise = null;
  }

  isValid() {
    return !!this.clientToken && this.expiresAt - Date.now() > EXPIRY_SKEW_MS;
  }

  invalidate() {
    this.clientToken = null;
    this.expiresAt = 0;
    this.inFlightPromise = null;
  }

  seed(clientToken, expiresAt) {
    if (!clientToken) {
      return;
    }
    this.clientToken = clientToken;
    this.expiresAt = expiresAt || 0;
    this.inFlightPromise = null;
  }

  notifySessionExpired(code = 'session_expired') {
    window.dispatchEvent(
      new CustomEvent('dspAuthExpired', {
        detail: { code },
      }),
    );
  }

  async refreshFromBackend() {
    try {
      const payload = await dspAuthService.getClientToken();
      this.clientToken = payload?.clientToken || null;
      this.expiresAt = payload?.expiresAt || 0;

      if (!this.clientToken) {
        throw new Error('No client token returned by /auth/token');
      }

      return this.clientToken;
    } catch (error) {
      if (
        error instanceof DSPAuthError &&
        (AUTH_EXPIRED_CODES.has(error.details?.code) || AUTH_EXPIRED_CODES.has(error.message))
      ) {
        this.invalidate();
        this.notifySessionExpired(error.details?.code || 'session_expired');
      }

      throw error;
    }
  }

  /**
   * The DSP host refused `rejectedToken`. Tell the backend: it either ends the
   * session (401 -> DSPAuthService fires `dspAuthExpired` and this throws) or
   * returns the session's current token, which is cached and returned.
   */
  async replaceRejected(rejectedToken) {
    this.invalidate();
    const payload = await dspAuthService.reportRejectedClientToken(rejectedToken);
    this.seed(payload?.clientToken, payload?.expiresAt);
    return this.clientToken;
  }

  async getClientToken(forceRefresh = false) {
    if (!forceRefresh && this.isValid()) {
      return this.clientToken;
    }

    if (this.inFlightPromise) {
      return this.inFlightPromise;
    }

    this.inFlightPromise = this.refreshFromBackend().finally(() => {
      this.inFlightPromise = null;
    });

    return this.inFlightPromise;
  }
}

export const clientTokenCache = new ClientTokenCache();
export default ClientTokenCache;
